import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { userDeletionService } from '../services/userDeletion.service';
import { Invitation, UserOption } from '../types/invitation';

interface UseUserDeletionOptions {
  onSuccessfulDelete?: () => void;
}

export const useUserDeletion = (options?: UseUserDeletionOptions) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [availableUsers, setAvailableUsers] = useState<UserOption[]>([]);
  const [currentInvitation, setCurrentInvitation] = useState<Invitation | null>(null);

  const openDeleteModal = async (invitation: Invitation) => {
    setIsLoading(true);
    setCurrentInvitation(invitation);

    try {
      const users = await userDeletionService.getUsersWithAcceptedInvitations();
      // Filter out the current user being deleted
      const filteredUsers = users.filter(user => user.id !== invitation.userId);

      if (filteredUsers.length === 0) {
        toast.error('No other users available to transfer content to');
        setIsLoading(false);
        return;
      }

      setAvailableUsers(filteredUsers);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching available users:', error);
      toast.error('Failed to fetch available users');
    } finally {
      setIsLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setCurrentInvitation(null);
    setAvailableUsers([]);
  };

  const deleteUser = async (transferToUserId: string) => {
    if (!currentInvitation?.id || !transferToUserId) return;

    setIsLoading(true);
    try {
      await userDeletionService.deleteUserAndTransferContent(
        currentInvitation.id,
        transferToUserId
      );

      toast.success('User deleted and content transferred successfully');

      if (options?.onSuccessfulDelete) {
        options.onSuccessfulDelete();
      }

      closeDeleteModal();
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user and transfer content');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isModalOpen,
    availableUsers,
    currentInvitation,
    openDeleteModal,
    closeDeleteModal,
    deleteUser
  };
};

export default useUserDeletion;
